import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  MenuButton,
  MenuGroup,
  MenuItem,
  SimpleList,
  SimpleListItem,
} from "@tiny/components";
import PlusCircle from "@tiny/icons/generated/PlusCircle";
import AddSegmentDialog from "../AddSegmentDialog";
import AudiencesListDialog from "../AudiencesListDialog";
import { useAddAudienceButton } from "./AddAudienceButton.hooks";
import TinyImportDialog, { TinyImporter } from "../TinyImportDialog";
import useTinyImporter from "../../hooks/useTinyImporter";
import useSuppressionImporter from "../AddSuppressionDialog/useSupperessionImporter";
import AddSuppressionDialog from "../AddSuppressionDialog";
import TinySuppressionImporter from "../AddSuppressionDialog/TinySuppressionImporter";
import ManageCustomAttributesDialog from "../ManageCustomAttributesDialog/ManageCustomAttributesDialog";
import { useTinyHost } from "@tiny/host-contract";

export interface SuppressionData {
  type: string;
  name: string;
  scope: string;
}

function AddAudienceButton({ isAudienceEmpty }: { isAudienceEmpty: boolean }) {
  const { capabilities } = useTinyHost();
  // Default (undefined) = visible; a host can set false to hide (TinyAlbert).
  const isSuppressionListVisible =
    capabilities.suppressionListVisible !== false;
  const isIntegrationsVisible = capabilities.integrationsVisible !== false;
  const [value, setValue] = React.useState<string | undefined>();
  const [suppressionDetails, setSuppressionDetails] = React.useState<
    SuppressionData | undefined
  >(undefined);
  const [manageAttrsOpen, setManageAttrsOpen] = React.useState(false);
  const {
    addIntegration,
    audienceDialogOpen,
    openAudienceDialog,
    closeAudienceDialog,
    mdUp,
    mobileOpen,
    openMobile,
    closeMobile,
    addSegmentDialogOpen,
    openAddSegmentDialog,
    closeAddSegmentDialog,
    onAddSegment,
    refetchAudience,
  } = useAddAudienceButton();

  const {
    openTinyImportDialog,
    closeTinyImportDialog,
    tinyImportDialogOpen,
    tinyImporterOpen,
    closeTinyImporter,
    openTinyImporter,
  } = useTinyImporter();

  const {
    suppressionImporterOpen,
    openSuppressionImporter,
    closeSuppressionImporter,
    addSuppressionDialogOpen,
    openAddSuppressionDialog,
    closeAddSuppressionDialog,
  } = useSuppressionImporter();

  return (
    <>
      {mdUp ? (
        <MenuButton
          text="Add new"
          variant="contained"
          fullWidth
          justifyContent="space-between"
        >
          <MenuGroup>SOURCES</MenuGroup>
          {mdUp && (
            <MenuItem onClick={openTinyImportDialog}>Upload file</MenuItem>
          )}
          {isIntegrationsVisible && (
            <MenuItem onClick={addIntegration}>Add integrations</MenuItem>
          )}
          {mdUp && !isAudienceEmpty && (
            <MenuItem onClick={openAudienceDialog}>Add to existing</MenuItem>
          )}
          <MenuGroup>SEGMENTS</MenuGroup>
          <MenuItem onClick={onAddSegment}>Add segment</MenuItem>
          {isSuppressionListVisible && (
            <MenuItem onClick={openAddSuppressionDialog}>
              Add suppression list
            </MenuItem>
          )}
          <MenuGroup>SETTINGS</MenuGroup>
          <MenuItem onClick={() => setManageAttrsOpen(true)}>
            Custom attributes
          </MenuItem>
        </MenuButton>
      ) : (
        <IconButton size="small" color="primary" onClick={openMobile}>
          <PlusCircle />
        </IconButton>
      )}
      <AudiencesListDialog
        open={audienceDialogOpen}
        onClose={closeAudienceDialog}
        onSuccess={refetchAudience}
        pageName="audienceListDialog"
      />
      <TinyImportDialog
        open={tinyImportDialogOpen}
        onClose={closeTinyImportDialog}
        onSuccess={(name) => {
          setValue(name);
          openTinyImporter();
        }}
      />
      <TinyImporter
        open={tinyImporterOpen}
        onClose={closeTinyImporter}
        value={value}
        onSuccess={refetchAudience}
      />
      <AddSegmentDialog
        open={addSegmentDialogOpen}
        onClose={closeAddSegmentDialog}
      />
      <AddSuppressionDialog
        open={addSuppressionDialogOpen}
        onClose={closeAddSuppressionDialog}
        onSuccess={(data) => {
          setSuppressionDetails(data);
          openSuppressionImporter();
        }}
      />
      <TinySuppressionImporter
        open={suppressionImporterOpen}
        onClose={closeSuppressionImporter}
        suppressionDetails={suppressionDetails}
        onSuccess={() => {
          console.log("Success!");
        }}
        suppressionType={suppressionDetails?.type}
      />
      <ManageCustomAttributesDialog
        open={manageAttrsOpen}
        onClose={() => setManageAttrsOpen(false)}
      />
      {!mdUp && (
        <Dialog open={mobileOpen} onClose={closeMobile} disableScrollLock>
          <DialogContent paddingTop={1} paddingBottom={3}>
            <SimpleList>
              {isIntegrationsVisible && (
                <SimpleListItem
                  onClick={() => {
                    addIntegration();
                    closeMobile();
                  }}
                  size="large"
                >
                  Add integrations
                </SimpleListItem>
              )}
              <SimpleListItem
                onClick={() => {
                  openAddSegmentDialog();
                  closeMobile();
                }}
                size="large"
              >
                Add segment
              </SimpleListItem>
            </SimpleList>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default AddAudienceButton;
